import type {
  FormKitNode,
  FormKitSchemaCondition,
  FormKitSchemaNode,
  FormKitSectionsSchema,
} from '@formkit/core'
import { clone } from '@formkit/utils'
import type { FormKitSchemaSection } from '@formkit/inputs'
import { findSection } from '@formkit/inputs'

export function createHelpBoxPlugin() {
  let currentlyShownHelpBox: string | undefined

  function showHelpBoxHandler(e: MouseEvent) {
    if (e.target === null) {
      return
    }

    e.preventDefault()

    let target: HTMLElement | null = null
    if (e.target instanceof HTMLElement) {
      target = e.target
    }

    const closestHelpBox = target?.closest(
      '[data-help-box-id]',
    ) as HTMLElement | null

    if (!closestHelpBox) {
      return
    }

    const helpBoxId = closestHelpBox.dataset.helpBoxId

    // We're keeping track of help boxes on the entire page.
    // In case there's a second FormKit form that also has help boxes,
    // we want them to collapse as well.
    if (currentlyShownHelpBox) {
      document
        .querySelector('#' + currentlyShownHelpBox)
        ?.classList.add('hidden')
    }

    if (currentlyShownHelpBox !== helpBoxId) {
      document.querySelector('#' + helpBoxId)?.classList.remove('hidden')
      currentlyShownHelpBox = helpBoxId
    } else {
      currentlyShownHelpBox = undefined
    }
  }

  return function (node: FormKitNode) {
    node.addProps(['helpTitle', 'helpClass'])

    node.on('created', () => {
      if (!node.props || !node.props.definition || !node.context) return

      node.context.handlers.showHelp = showHelpBoxHandler

      const inputDefinition = clone(node.props.definition)
      const originalSchema = inputDefinition.schema
      if (typeof originalSchema !== 'function') {
        return
      }

      const higherOrderSchema = (extensions: FormKitSectionsSchema) => {
        // Remove the existing help boxes, so we can move it outside the element.
        extensions.help = {
          if: 'false',
          $el: 'div',
        }

        extensions.optionHelp = {
          if: 'false',
          $el: 'div',
        }

        const helpBoxIdSchema = {
          if: '$option.value && $type != "dropdown"',
          then: `$: $node.props.id + '-' + $option.value + '-help'`,
          else: `$: $node.props.id + '-help'`,
        }

        const helpBoxSchema = (prefix: string = '') => ({
          if: `$${prefix}help!= undefined`,
          $el: 'div',
          attrs: {
            id: helpBoxIdSchema,
            class: {
              if: `$${prefix}helpClass`,
              then: `$: 'help-box hidden ' + $${prefix}helpClass`,
              else: 'help-box hidden col-span-2',
            },
          },
          children: [
            {
              $el: 'h4',
              children: {
                if: `$${prefix}helpTitle`,
                then: `$${prefix}helpTitle`,
                else: `$${prefix}label`,
              },
            },
            {
              $el: 'div',
              attrs: {
                innerHTML: `$${prefix}help`,
              },
            },
          ],
        })

        const schemaSoFar = originalSchema(extensions)
        const finalSchema = Array.isArray(schemaSoFar)
          ? schemaSoFar[0]
          : schemaSoFar

        const labelLikeSections = [
          findSection(finalSchema, 'label')[0],
          findSection(finalSchema, 'legend')[0],
        ]
          .filter(Boolean)
          .flat() as FormKitSchemaSection[]

        if (labelLikeSections.length > 0) {
          labelLikeSections.forEach((section: FormKitSchemaSection) => {
            if (
              section.else.meta?.section === 'label' ||
              section.else.meta?.section === 'legend'
            ) {
              ;(section.else.children as FormKitSchemaNode[]).push({
                if: '($option != undefined && $type != "dropdown" && $option.help) || ($option == undefined && $help) || ($type == "dropdown" && $help)',
                $el: 'button',
                attrs: {
                  onClick: `$handlers.showHelp`,
                  class: 'info',
                  'data-help-box-id': helpBoxIdSchema,
                },
                children: [
                  {
                    $el: 'span',
                    attrs: {
                      class: 'sr-only',
                    },
                    children: ['Hilfe anzeigen'],
                  },
                  {
                    $el: 'span',
                    children: ['i'],
                    attrs: {
                      'aria-hidden': true,
                    },
                  },
                ],
              })
            }
          })
        }

        const [optionsSections] = findSection(finalSchema, 'options') as (
          | FormKitSchemaSection[]
          | false
        )[]

        if (optionsSections) {
          ;(optionsSections[2] as FormKitSchemaCondition).else = {
            $el: 'div',
            children: [
              (optionsSections[2] as FormKitSchemaCondition).else,
              {
                for: ['option', '$options'],
                ...helpBoxSchema('option.'),
              },
            ],
          } as FormKitSchemaNode
        }

        return {
          $el: null,
          children: [finalSchema, helpBoxSchema()],
        }
      }

      inputDefinition.schema = higherOrderSchema
      if (inputDefinition.schemaMemoKey) {
        inputDefinition.schemaMemoKey += '-help-box'
      }
      node.props.definition = inputDefinition
    })
  }
}
